/**
 * As abas da mesa única (E205.F1, ADR-0147 §1; estudo `docs/design/mesa-em-abas-2026-11-01.md` §2.2).
 *
 * A aba ativa vai no hash do endereço, por esta tabela fixa, para que todo link de hoje (o próximo
 * passo, o painel do dado, os redirecionamentos antigos, o "Alterar na mesa" do documento) continue
 * chegando ao lugar certo. A âncora de um bloco de dentro de uma aba (o campo do banco, a seção do
 * documento, o envio) escolhe a aba e depois rola até o bloco.
 */
import { PREFIXO_DA_ANCORA } from '@/lib/documentoDaSaida';

export const ABAS_DA_MESA = [
  'exame',
  'pendencias',
  'documentos',
  'fotografias',
  'conclusao',
] as const;

export type AbaDaMesa = (typeof ABAS_DA_MESA)[number];

export const ABA_PADRAO: AbaDaMesa = 'exame';

/** E205.F3: a âncora da linha de um documento na aba Documentos (`documento-<id>`). */
export const PREFIXO_DA_LINHA_DO_DOCUMENTO = 'documento-';

export function ancoraDoDocumento(documentoId: string): string {
  return `${PREFIXO_DA_LINHA_DO_DOCUMENTO}${documentoId}`;
}

const ABA_DA_ANCORA: Readonly<Record<string, AbaDaMesa>> = {
  exame: 'exame',
  'informar-o-banco': 'exame',
  pendencias: 'pendencias',
  documentos: 'documentos',
  'adicionar-documentos': 'documentos',
  leitura: 'documentos',
  protocolo: 'documentos',
  // E203.F1: as âncoras antigas do material (a área, a preparação) caem em Documentos.
  material: 'documentos',
  preparacao: 'documentos',
  fotografias: 'fotografias',
  // E209.F2.T09: a linha da saída no card do pacote (o "Retomar" da que falhou).
  'pacote-exame': 'exame',
  'pacote-riscos': 'exame',
  conclusao: 'conclusao',
  'conclusao-da-ccb': 'conclusao',
  formalizacao: 'conclusao',
  portal: 'conclusao',
};

/** É o nome de uma aba (o clique na aba grava o hash com ele; não rola a página). */
export function eAba(ancora: string | null): ancora is AbaDaMesa {
  return ancora !== null && (ABAS_DA_MESA as readonly string[]).includes(ancora);
}

/**
 * A aba que a âncora do endereço escolhe. Sem âncora, a aba padrão (Exame); a seção do documento é
 * do Exame; âncora fora da tabela devolve `null` (quem chama procura o bloco na página ou fica onde
 * está).
 */
export function abaDaAncora(ancora: string | null): AbaDaMesa | null {
  if (ancora === null || ancora === '') return ABA_PADRAO;
  if (ancora.startsWith(PREFIXO_DA_ANCORA)) return 'exame';
  // E205.F3: a linha de um documento na aba Documentos (o alvo das pendências informativas).
  if (ancora.startsWith(PREFIXO_DA_LINHA_DO_DOCUMENTO)) return 'documentos';
  return ABA_DA_ANCORA[ancora] ?? null;
}

/** O atributo que marca o painel de cada aba: a âncora fora da tabela acha a aba do bloco por ele. */
export const ATRIBUTO_DA_ABA = 'data-aba-da-mesa';

/** A aba do painel que contém o elemento; `null` fora das abas. */
export function abaDoElemento(elemento: Element | null): AbaDaMesa | null {
  const valor = elemento?.closest(`[${ATRIBUTO_DA_ABA}]`)?.getAttribute(ATRIBUTO_DA_ABA) ?? null;
  return eAba(valor) ? valor : null;
}
